import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, Star, MapPin, Package, TrendingUp, Store, Loader2 } from "lucide-react";
import { WhatsAppIcon } from "@/components/ui/whatsapp-icon";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface SellerProfile {
  id: string;
  business_name: string | null;
  business_description: string | null;
  business_category: string | null;
  rating: number | null;
  verified: boolean | null;
  avatar_url: string | null;
  created_at: string | null;
}

const Sellers = () => {
  const [sellers, setSellers] = useState<SellerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const topCategories = [
    "Electronics", "Fashion", "Home & Living", "Beauty", "Sports", "Food & Beverages"
  ];

  useEffect(() => {
    const fetchSellers = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase.rpc('get_safe_seller_profiles');
        
        if (error) throw error;
        setSellers(data || []);
      } catch (error) {
        console.error('Error fetching sellers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSellers();
  }, []);

  const filteredSellers = sellers.filter(seller => {
    const matchesSearch = !searchQuery || 
      seller.business_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      seller.business_category?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = !selectedCategory || 
      seller.business_category?.toLowerCase().includes(selectedCategory.toLowerCase());
    
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Trusted Sellers</h1>
        <p className="text-muted-foreground">Discover verified sellers with quality products</p>
      </div>

      {/* Search and Filter */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input 
              placeholder="Search sellers by name or category..." 
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {topCategories.map((cat, index) => (
              <Badge 
                key={index} 
                variant={selectedCategory === cat ? "default" : "outline"}
                className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
              >
                {cat}
              </Badge>
            ))}
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredSellers.length === 0 && (
        <Card className="p-8 text-center">
          <Store className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No sellers found</h3>
          <p className="text-muted-foreground">
            {searchQuery || selectedCategory 
              ? "Try adjusting your search or filter criteria"
              : "Be the first to start selling on SabiTradeNG!"}
          </p>
        </Card>
      )}

      {/* Sellers Grid */}
      {!loading && filteredSellers.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredSellers.map((seller) => (
            <Card key={seller.id} className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center overflow-hidden">
                  {seller.avatar_url ? (
                    <img 
                      src={seller.avatar_url} 
                      alt={`Profile image for ${seller.business_name || 'seller'}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Store className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold">{seller.business_name || 'Unnamed Store'}</h3>
                    {seller.verified && (
                      <Badge variant="default" className="text-xs">Verified</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{seller.business_category || 'General'}</p>
                  {seller.rating && seller.rating > 0 && (
                    <div className="flex items-center gap-1 mt-1">
                      <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                      <span className="text-sm font-medium">{seller.rating.toFixed(1)}</span>
                    </div>
                  )}
                </div>
              </div>

              {seller.business_description && (
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {seller.business_description}
                </p>
              )}

              <div className="flex gap-2">
                <Link to={`/products?seller=${seller.id}`} className="flex-1">
                  <Button variant="outline" size="sm" className="w-full">
                    View Products
                  </Button>
                </Link>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Become a Seller CTA */}
      <Card className="p-8 bg-gradient-to-r from-primary/10 to-primary/5">
        <div className="text-center">
          <TrendingUp className="h-12 w-12 text-primary mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-3">Start Selling on SabiTradeNG</h2>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Join thousands of successful sellers. Set up your shop in minutes and reach customers across Nigeria through WhatsApp commerce.
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/auth">
              <Button size="lg">Become a Seller</Button>
            </Link>
            <Link to="/help-center">
              <Button size="lg" variant="outline">Learn more about selling</Button>
            </Link>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Sellers;